import { useState, useEffect } from 'react';
import { WEB_SCHEMA } from '../../../../../constants/componentStaticData';
import { GuidesAndLinksData } from '../../../../../constants/partnerPageConst';
import { Meta } from '../../../../../layout/Meta';
import { handleTrack } from '../../../../../utils/tracker';
import { MarktechModal } from '../../../../Atoms/Modal';
import { generateSSOLinkWithReturnPath } from '../../../../cms/layout/comment-section/utils/utils';
import { b2cData } from '../../../../cms/layout/components/header';
import { FaqComponent } from '../../../../Common/FAQS';
import { GuidesAndUsefullLink } from '../../../../Common/GuidesAndUsefullLink';
import { GreySection } from '../../../../Common/Section/GreySection';
import { LoginButton } from '../../../../Header/LoginButton';
import { ClearInNavbar } from '../../../../Navbar/ClearNavbar';
import { useHashScroll } from '../../../../partner-pages/hooks/useScrollHook';
import { PartnerRewards } from '../../../../partner-pages/page-sections/rewards-section/partner-rewards';
import { pricingFaq } from '../../constants';
import { useDataForPlansPricing } from '../../pricing.hooks';
import { IPricing, TEASPlans } from '../../types';
import { DrawerLeadForm } from '../drawer-form/drawer_form';
import { getEASPlanList } from '../eas_fold/utils';
import { NewHeroComponent } from './new-hero-fold.component';
import { Questionaire } from '../interactive_faq_fold/Questionaire';
import { ShowCasedFold } from '../showcased_fold';
import { TestimonialFold } from '../testimonial_fold';
import { PricingCardWrapperV1 } from './pricing-card-carousel-v1.component';
import {
  PricingCardStaticDetailsV1_1,
  PricingCardStaticDetailsV1_2,
  additionalPlans,
} from './card-details-constants-v1';

export const NewPricingPage: React.FC<IPricing> = ({
  PAID_PAGE = false,
  pricePageData,
}): JSX.Element => {
  const existingPlans = pricePageData?.eas_plans?.categories?.[0]?.plans || [];
  const allPlans = [...additionalPlans, ...existingPlans];

  const updatedPricePageData = pricePageData
    ? {
        ...pricePageData,
        eas_plans: {
          ...pricePageData.eas_plans,
          categories:
            pricePageData?.eas_plans?.categories?.map((category, index) =>
              index === 0 ? { ...category, plans: allPlans } : category
            ) || [],
        },
      }
    : undefined;

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [loginLink, setLoginLink] = useState('');
  useHashScroll(69);
  useEffect(() => {
    const returnPath = `/s/pricing`;
    const loginLink = generateSSOLinkWithReturnPath(returnPath, 'cmsPage');
    setLoginLink(loginLink);
  }, []);

  const pricingPlanData = useDataForPlansPricing(
    updatedPricePageData?.eas_plans
      ? getEASPlanList(updatedPricePageData.eas_plans as TEASPlans)
      : []
  );

  if (!pricePageData) {
    return <></>;
  }

  const pricingPlanDataV1_1 = pricingPlanData.slice(0, 4);
  const pricingPlanDataV1_2 = pricingPlanData.slice(4, 8);

  const toggleIsOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      handleTrack({
        label: 'chat-open',
      });
    }
  };
  return (
    <>
      <Meta
        canonical="/s/pricing/"
        title={pricePageData.metaTitle}
        description={pricePageData.metaDescription}
        keywords={pricePageData.metaKeywords}
        key="robots"
        noindex={PAID_PAGE}
        nofollow={PAID_PAGE}
      />
      <ClearInNavbar
        data={{
          ...b2cData,
          rightCTA: <LoginButton link={loginLink} />,
        }}
      />
      <div>
        <div className="pricingCardComponent wg-container">
          <NewHeroComponent />
        </div>

        <div className="px-20 sm:px-4 pt-8 pb-1 text-s-32 font-semibold sm:text-center sm:mt-4 sm:text-s-32">
          Belated ITR Filing Plans : ITR-U
          {/* Desktop text */}
          <div className="block sm:hidden text-s-18 font-normal">
            Updated returns can be filed{' '}
            <span className="font-bold">only if additional tax</span> is
            payable.
          </div>
          <div className="block sm:hidden text-s-18 font-normal">
            Refund cases or zero-tax cases are{' '}
            <span className="font-bold">not eligible</span> for filing under
            ITR-U.
          </div>
          {/* Mobile text */}
          <div className="hidden sm:block text-s-18 font-normal">
            ITR-U can be filed{' '}
            <span className="font-bold">only if additional tax</span> is
            payable.
          </div>
          <div className="hidden sm:block text-s-18 font-normal">
            Refund or zero-tax cases are{' '}
            <span className="font-bold">not eligible</span> for filing under
            ITR-U.
          </div>
        </div>
        <PricingCardWrapperV1
          pricingPlanData={pricingPlanDataV1_2}
          PricingCardStaticDetails={PricingCardStaticDetailsV1_2}
          classname="w-full px-20 mt-[-40px] sm:px-4 sm:py-6"
        />
        <div className="px-20 sm:px-4 pt-8 pb-1 text-s-32 font-semibold sm:text-center sm:mt-4 sm:text-s-32">
          Advisory Services
        </div>
        <PricingCardWrapperV1
          pricingPlanData={pricingPlanDataV1_1}
          PricingCardStaticDetails={PricingCardStaticDetailsV1_1}
          classname="w-full px-20 mt-[-40px] sm:px-4 sm:py-6"
        />
        <DrawerLeadForm
          toggleIsOpen={toggleIsOpen}
          isOpen={isOpen}
          partnerName="pricing-page"
        />
        <div className="wg-container">
          <ShowCasedFold showcaseFold={pricePageData.showcased_plan} />
        </div>
      </div>
      <PartnerRewards />
      <div className="wg-container">
        <TestimonialFold />
      </div>

      <GreySection>
        <GuidesAndUsefullLink
          heading={GuidesAndLinksData.heading}
          cards={GuidesAndLinksData.cards}
        />
      </GreySection>

      <div className="wg-container">
        <FaqComponent
          heading="FAQs"
          variant="partner"
          expanded="firstOnly"
          FAQS={pricingFaq}
          faqIconColor={'#314259'}
          isShowSchema={true}
        />
      </div>
      <MarktechModal
        open={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        closeIcon={'✕'}
        classNames={{
          modal:
            'qnaMaxWidth mobile-bottom-fixed mobile-search-box rounded-2xl h-fit max-h-[95vh] overflow-hidden w-full',
          closeButton: 'text-s-24',
        }}
      >
        <div className="modal rounded-xl modal-open bg-[#F9FAFB]">
          <Questionaire
            leadDetails={{
              type: 'plan-selection-form',
              sheetName: 'PlanSelectionQA',
            }}
          />
        </div>
      </MarktechModal>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            WEB_SCHEMA({
              title: pricePageData.metaTitle,
              metaDesc: pricePageData.metaDescription,
              url: 'https://cleartax.in/s/pricing/',
            })
          ),
        }}
      ></script>
    </>
  );
};
